import React from "react";
import intl from "react-intl-universal";
import {override, makeObservable} from "mobx";
import {WorkspaceUser} from '../../API';
import {FilterSchema} from "./DataSource";
import {TableDataSource} from "./TableDataSource";
import {ColId, ColSchema, Row} from "../../ui/components/Table";

import {listWorkspaceUsersByWsId} from "./requests/listWorkspaceUsers";
import {formatDate} from "../../utils/date";
import {UserCard} from "../../ui/components/UserCard";
import {WsUserActions} from "../../ui/screens/WorkspaceSettings/WorkspaceRemoveUser";
import {Tooltip} from "../../ui/components/Tooltip";
import {getFullName, getFullNameOrEmpty} from "../../utils/userHelper";

import {onCreateWorkspaceUser, onDeleteWorkspaceUser, onUpdateWorkspaceUser} from "../../graphql/subscriptions";
import {FullDataSourceDeps, UserDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";

const colOrder: ColId[] = [
    'name',
    'email',
    'lastActive',
    'role',
    'actions',
];

const colSchema: ColSchema = {
    name: {
        getTitle: () => intl.get('members.tableCols.name').d('Name'),
        alignLeft: true,
        minWidth: 80,
        maxWidth: 470,
    },
    email: {
        minWidth: 120,
        getTitle: () => intl.get('members.tableCols.email').d('Email'),
        alignLeft: true
    },
    lastActive: {
        minWidth: 80,
        getTitle: () => intl.get('members.tableCols.lastActive').d('Last Active'),
        alignLeft: true
    },
    role: {
        getTitle: () => intl.get('members.tableCols.role').d('Role'),
        minWidth: 120,
        maxWidth: 178,
        alignLeft: true
    },
    actions: {
        getTitle: () => '',
        minWidth: 56,
        maxWidth: 56
    },
};

export class WsUsersDataSource extends TableDataSource<WorkspaceUser, WorkspaceUser, FullDataSourceDeps> {
    readonly _modelName = 'WorkspaceUser' as const;
    readonly _subscriptionGQLs = {
        onCreate: onCreateWorkspaceUser,
        onUpdate: onUpdateWorkspaceUser,
        onDelete: onDeleteWorkspaceUser,
    } as const
    wsId: string;

    get syncHash() {
        return `ws` + this.wsId.slice(0,6);
    }

    getWsClient() {
        return this.deps.dataClientService.getWsClientForTable('workspaceUsers');
    };

    constructor(deps: FullDataSourceDeps, wsId: string) {
        super(deps);

        makeObservable(this, {
            allRows: override,
        });

        this.wsId = wsId;
    }

    getColsSchema(): ColSchema {
        return colSchema;
    }

    getColOrder(): ColId[] {
        return colOrder;
    }

    get allRows() {
        return [
            {
                __typename: "WorkspaceUser",
                ownerId: this.deps.userState.tenantId,
                workspaceId: this.wsId,
                userId: this.deps.userState.ownerUserId,
                role: 'owner',
                id: '0',
                _version: 0,
                _lastChangedAt: 0,
            } as WorkspaceUser,
            ...this._allRows
        ];
    }

    getFilterableValue = (field: string, wsUser: WorkspaceUser) => {
        const user = this.deps.userState.usersDS.users.get(wsUser.userId);
        switch (field) {
            case 'name':
                return getFullNameOrEmpty(user);
            case 'lastActive':
                return user.lastActive;
            case 'email':
                return user.email;
            case 'role':
                return this.deps.userState.getUserRole(user);
            default:
                return wsUser[field as keyof WorkspaceUser]
        }
    }

    mapResultToTableRow = (wsUser: WorkspaceUser): Row => {
        const user = this.deps.userState.usersDS.users.get(wsUser.userId);
        const currentUser = this.deps.userState.currentUserDb
        const formattedDate = user.lastActive
            ? formatDate(this.deps.rootStore.currentLocale, new Date(user.lastActive as string))
            : {date: intl.get('members.noActivity').d('No activity'), isToday: false};
        const isUserOnline = this.deps.userState.activityProvider.isUserActive(user.id);

        const userRole = this.deps.userState.getUserRole(user);

        const renderUserRoleLabel = (userRole: string) => {
            switch (userRole) {
                case 'owner':
                    return intl.get('user-role.owner').d('Owner');
                case 'admin':
                    return intl.get('user-role.admin').d('Admin');
                case 'reader':
                    return intl.get('user-role.reader').d('Reader');
                case 'editor':
                    return intl.get('user-role.editor').d('Editor');
                default:
                    return;
            }
        };
        return {
            id: user.id,
            name: (
                <div className="Members_row-card">
                    <UserCard user={user} size={36} isEmail={false}/>&nbsp;{currentUser?.id === user.id &&
                    <span>{intl.get('members.selfUser').d('(you)')}</span>}
                </div>
            ),
            email: user.email,
            lastActive: (
                isUserOnline ?
                    <span className="Member__online">{intl.get('common.online').d('Online')}</span>
                    : formattedDate.date
            ),
            role: (
                <Tooltip
                    title={intl.getHTML('wsMembers.role.change.description').d('You can change user roles in <b>Company Settings > Members</b>')}
                    maxWidth={200}>
                    <div className="Members__Owner">
                        {renderUserRoleLabel(userRole)}
                    </div>
                </Tooltip>
            ),
            actions: userRole !== 'owner' ?
                <WsUserActions user={user} wsUser={wsUser} className={"Members__delete"}/> : " ",
            raw: user,
        };
    }

    getFilterDefaultValues(): {} {
        return {}
    }

    getFilterSchema(): FilterSchema<WorkspaceUser> {
        return {
            closedAt: {
                type: 'client',
                apply: (wsUser: WorkspaceUser) => {
                    const user = this.deps.userState.usersDS.users.get(wsUser.userId);
                    return !user.closedAt;
                }
            },
            name: {
                type: 'client',
                apply: (wsUser: WorkspaceUser) => {
                    const user = this.deps.userState.usersDS.users.get(wsUser.userId);
                    return getFullName(user)?.toLowerCase().includes(this.filterValues.name?.toLowerCase() || '')
                }
            },
            role: {
                type: 'client',
                apply: (wsUser: WorkspaceUser) => {
                    const user = this.deps.userState.usersDS.users.get(wsUser.userId);
                    return this.filterValues.role ? this.deps.userState.getUserRole(user) === this.filterValues.role : true
                }
            }
        };
    }

    loadGQLFetch() {
        return listWorkspaceUsersByWsId(
            this.deps.dataClientService,
            this.deps.authService,
            this.deps.userState.tenantId,
            this.wsId
        );
    };

    loadMoreGQLFetch(nextToken: string) {
        return listWorkspaceUsersByWsId(
            this.deps.dataClientService,
            this.deps.authService,
            this.deps.userState.tenantId,
            this.wsId, 
            undefined, 
            nextToken
        );
    };
}
